import React from 'react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';

import {
  ArrowLeft,
  Printer,
  Download,
  Share2,
  Trash2,
  Edit,
  Check
} from 'lucide-react';

const styles = {
  container: { height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#F3F4F6', position: 'relative' },
  modalOverlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  confirmBox: { background: 'white', borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '340px', display: 'flex', flexDirection: 'column', gap: '16px' },
  confirmTitle: { fontSize: '18px', fontWeight: '800', color: '#0F172A', margin: 0 },
  confirmText: { fontSize: '14px', color: '#64748B', lineHeight: '1.5', margin: 0 },
  confirmActions: { display: 'flex', gap: '12px' },
  cancelBtn: { flex: 1, height: '40px', borderRadius: '10px', border: '1px solid #E2E8F0', background: 'white', color: '#64748B', fontWeight: '700', fontSize: '13px', cursor: 'pointer' },
  deleteBtn: { flex: 1, height: '40px', borderRadius: '10px', border: 'none', background: '#EF4444', color: 'white', fontWeight: '700', fontSize: '13px', cursor: 'pointer' },
  nav: { height: '56px', backgroundColor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', borderBottom: '1px solid #E2E8F0', flexShrink: 0 },
  backBtn: { background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '12px', fontSize: '18px', fontWeight: 'bold', color: '#111827', cursor: 'pointer' },
  navRight: { display: 'flex', gap: '16px' },
  navIcon: { background: 'none', border: 'none', color: '#4B5563', cursor: 'pointer' },
  content: { flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'row' },
  scrollArea: { flex: 1, overflowY: 'auto', padding: '24px', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', backgroundColor: '#F8FAFC' },
  paper: { width: '100%', maxWidth: '800px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', overflow: 'hidden', position: 'relative' },
  accentStrip: { height: '4px', width: '100%' },
  premiumContent: { padding: '24px' },
  premiumHeader: { display: 'flex', justifyContent: 'space-between', marginBottom: '24px' },
  pHeaderLeft: { display: 'flex', gap: '16px', alignItems: 'center' },
  pLogoContainer: { width: '56px', height: '56px', borderRadius: '50%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  pLogo: { width: '100%', height: '100%', objectFit: 'cover' },
  pLogoPlaceholder: { width: '100%', height: '100%', color: 'white', fontWeight: 'bold', fontSize: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  pBizInfo: { display: 'flex', flexDirection: 'column' },
  pBizName: { fontSize: '18px', fontWeight: 'bold', margin: 0 },
  pBizMeta: { fontSize: '11px', color: '#6B7280', margin: '2px 0', lineHeight: '1.4' },
  pHeaderRight: { textAlign: 'right' },
  pLabel: { fontSize: '16px', fontWeight: 'bold', margin: '0 0 8px 0' },
  pMeta: { fontSize: '11px', color: '#4B5563' },
  pBillTo: { display: 'flex', padding: '16px', borderRadius: '12px', marginBottom: '24px', backgroundColor: '#EFF6FF', border: '1px solid #E2E8F0' },
  pBtLeft: { flex: 1 },
  pBtSub: { fontSize: '10px', color: '#9CA3AF', textTransform: 'uppercase', display: 'block', marginBottom: '4px' },
  pCustName: { fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' },
  pCustMeta: { fontSize: '11px', color: '#6B7280', margin: '2px 0', lineHeight: '1.4' },
  pBtRight: { textAlign: 'right', width: '150px' },
  pGrandTotal: { fontSize: '22px', fontWeight: 'bold', margin: '4px 0', color: '#111827' },
  pWords: { fontSize: '9px', color: '#6B7280', fontStyle: 'italic', lineHeight: '1.2' },
  pTable: { marginBottom: '16px' },
  pTh: { display: 'flex', borderBottom: '1px solid #E2E8F0', padding: '8px 0', fontSize: '11px', color: '#6B7280', fontWeight: 'bold' },
  pTr: { display: 'flex', borderBottom: '1px solid #F3F4F6', padding: '12px 0', fontSize: '12px' },
  pSubtotalTr: { display: 'flex', borderTop: '1px solid #E2E8F0', padding: '10px 0', fontSize: '12px' },

  taxSlabBox: { marginBottom: '20px' },
  taxSlabHeader: { display: 'flex', borderBottom: '1px solid #E2E8F0', padding: '6px 0', fontSize: '11px', fontWeight: 'bold', color: '#6B7280' },
  taxSlabRow: { display: 'flex', padding: '4px 0', fontSize: '11px', color: '#4B5563' },

  pSummaryArea: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '20px', position: 'relative' },
  pSumLeft: { flex: 1, paddingRight: '20px' },
  pTermsTitle: { fontSize: '11px', fontWeight: 'bold', color: '#4B5563', marginBottom: '4px' },
  pTermsText: { fontSize: '10px', color: '#6B7280', lineHeight: '1.4', marginBottom: '20px', whiteSpace: 'pre-wrap' },
  pDigitalMsg: { fontSize: '10px', color: '#9CA3AF', fontStyle: 'italic', marginBottom: '10px', textAlign: 'center', width: '100%' },
  pTrust: { fontSize: '11px', color: '#6B7280' },
  pSumRight: { width: '250px' },
  pSumRow: { display: 'flex', justifyContent: 'space-between', fontSize: '12px', padding: '4px 0' },
  pSigBox: { textAlign: 'center', marginTop: '24px' },
  pSigLine: { width: '120px', borderTop: '1px solid #111827', margin: '0 auto 4px auto' },
  pSigLabel: { fontSize: '10px', fontWeight: 'bold' },
  pPaidStamp: { position: 'absolute', right: '80px', bottom: '30px', transform: 'rotate(-20deg)', pointerEvents: 'none' },
  pStampCircle: { width: '70px', height: '70px', borderRadius: '50%', border: '4px double #10B981', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10B981', fontWeight: '900' },
  pStampText: { fontSize: '20px', border: '2px solid #10B981', padding: '2px 8px', borderRadius: '4px' },

  // Thermal
  thermalPaper: { width: '280px', backgroundColor: 'white', padding: '16px', fontSize: '12px', fontFamily: 'monospace' },
  tBizName: { textAlign: 'center', fontSize: '16px', fontWeight: 'bold', margin: '0 0 4px 0' },
  tBizMeta: { textAlign: 'center', fontSize: '10px', margin: '2px 0' },
  tDivider: { textAlign: 'center', margin: '8px 0', fontSize: '10px' },
  tCenter: { textAlign: 'center' },
  tMetaRow: { display: 'flex', justifyContent: 'space-between', fontSize: '10px' },
  tTh: { display: 'flex', fontWeight: 'bold', fontSize: '10px' },
  tTr: { display: 'flex', marginTop: '4px', fontSize: '10px' },
  tSumRow: { display: 'flex', justifyContent: 'space-between', marginTop: '4px', fontSize: '10px' },
  tFooter: { textAlign: 'center', marginTop: '16px', fontSize: '10px' },

  // Basic
  basicHeader: { display: 'flex', justifyContent: 'space-between', padding: '24px' },
  bHeaderLeft: { flex: 1 },
  bBizName: { fontSize: '18px', fontWeight: 'bold', margin: 0 },
  bBizMeta: { fontSize: '11px', color: '#6B7280', margin: '2px 0' },
  bHeaderRight: { textAlign: 'right' },
  bLabel: { fontSize: '14px', fontWeight: 'bold', margin: '0 0 4px 0' },
  bMeta: { fontSize: '11px', color: '#6B7280' },
  bBillTo: { padding: '16px', margin: '0 24px 24px 24px', border: '1px solid #E2E8F0' },
  bBtTitle: { fontSize: '11px', fontWeight: 'bold', color: '#6B7280', marginBottom: '8px' },
  bTable: { padding: '0 24px' },
  bTh: { display: 'flex', padding: '8px', fontSize: '11px', fontWeight: 'bold' },
  bTr: { display: 'flex', padding: '10px 8px', borderBottom: '1px solid #F3F4F6', fontSize: '11px' },
  bSummary: { padding: '16px', margin: '24px', border: '1px solid #E2E8F0' },
  bSumRow: { display: 'flex', justifyContent: 'space-between', fontSize: '12px', padding: '4px 0' },
  bDivider: { height: '1px', backgroundColor: '#E2E8F0', margin: '8px 0' },
  bWords: { fontSize: '10px', color: '#9CA3AF', fontStyle: 'italic', marginTop: '12px' },

  // Controls
  controls: { width: '360px', backgroundColor: 'white', padding: '24px', borderLeft: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column', flexShrink: 0, overflowY: 'auto' },
  themeSelector: { display: 'flex', backgroundColor: '#F1F5F9', padding: '6px', borderRadius: '32px', marginBottom: '24px' },
  tBtn: { flex: 1, padding: '10px', border: 'none', borderRadius: '28px', fontSize: '14px', fontWeight: '600', color: '#64748B', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' },
  tBtnActive: { backgroundColor: 'white', color: '#1D4ED8', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' },
  newBadge: { backgroundColor: '#EF4444', color: 'white', fontSize: '8px', padding: '2px 6px', borderRadius: '8px', textTransform: 'uppercase' },
  colorPicker: { display: 'flex', justifyContent: 'center', gap: '16px', marginBottom: '32px' },
  colorCircle: { width: '36px', height: '36px', borderRadius: '50%', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  actionButtons: { display: 'flex', justifyContent: 'space-between', marginBottom: 'auto', paddingBottom: '32px' },
  actionItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', background: 'none', border: 'none', cursor: 'pointer' },
  circleAction: { width: '56px', height: '56px', borderRadius: '50%', backgroundColor: '#EFF6FF', color: '#1D4ED8', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none' },
  bottomNav: { display: 'flex', gap: '16px', marginTop: '24px' },
  secondaryBtn: { flex: 1, height: '48px', borderRadius: '12px', border: '2px solid #1D4ED8', color: '#1D4ED8', background: 'white', fontWeight: 'bold', fontSize: '16px', cursor: 'pointer' },
  primaryBtn: { flex: 1, height: '48px', borderRadius: '12px', border: 'none', background: '#1D4ED8', color: 'white', fontWeight: 'bold', fontSize: '16px', cursor: 'pointer' }
};

const formatCurrency = (amount) => {
  if (isNaN(amount) || amount === null || amount === undefined) return '₹0';

  let cleanAmount = amount;
  const rounded = Math.round(amount);
  if (Math.abs(amount - rounded) < 0.02) {
    cleanAmount = rounded;
  }

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: cleanAmount % 1 === 0 ? 0 : 2,
  }).format(cleanAmount);
};

const formatRate = (rate) => {
  if (!rate || isNaN(rate)) return '0';
  if (rate % 1 === 0) return rate.toString();
  return rate.toFixed(2).replace(/\.00$/, '');
};

const formatDate = (date) => {
  if (!date) return '-';
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch (e) {
    return '-';
  }
};

const numberToWords = (num) => {
  if (num === 0) return 'Zero';
  if (!num || isNaN(num) || !isFinite(num)) return '';
  const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const convert = (n) => {
    if (isNaN(n) || n < 0) return '';
    if (n < 20) return a[Math.floor(n)];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + a[n % 10] : '');
    if (n < 1000) return a[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' and ' + convert(n % 100) : '');
    if (n < 100000) return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 !== 0 ? ' ' + convert(n % 1000) : '');
    if (n < 10000000) return convert(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 !== 0 ? ' ' + convert(n % 100000) : '');
    return convert(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 !== 0 ? ' ' + convert(n % 10000000) : '');
  };
  return convert(Math.floor(num)) + ' Rupees Only';
};

const formatAddr = (addr) => {
  if (!addr) return '';
  if (typeof addr === 'string') {
    if (addr.startsWith('{')) {
      try {
        const obj = JSON.parse(addr);
        return formatAddr(obj);
      } catch (e) { return addr; }
    }
    return addr;
  }
  const parts = [
    addr.address,
    addr.full_address,
    addr.area,
    addr.city,
    addr.state,
    addr.pincode,
    addr.zip
  ].filter(Boolean);

  if (parts.length === 0) {
    // If it's an object but we couldn't find common fields, 
    // try to see if it's a simple object with a single value or just return the string representation
    const values = Object.values(addr).filter(v => typeof v === 'string' && v.length > 0);
    if (values.length > 0) return values.join(', ');
    return '';
  }
  return parts.join(', ');
};

const InvoicePreviewView = ({ bill, businessProfile, onBack, onDelete, onEdit, onCreateNew }) => {
  const { t } = useLang();
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const [selectedTheme, setSelectedTheme] = React.useState('premium');
  const [accentColor, setAccentColor] = React.useState('#EF4444');

  /*
    switch (lang) {
      case 'hi': return 'अतिरिक्त छूट';
      case 'kn': return 'ಹೆಚ್ಚುವರಿ ರಿಯಾಯಿತಿ';
      case 'mr': return 'अतिरिक्त सवलत';
      case 'te': return 'అదనపు డిస్కౌంట్';
      case 'ta': return 'கூடுதல் தள்ளுபடி';
      case 'pa': return 'ਵਾਧੂ ਛੋਟ';
      case 'gu': return 'વધારાની છૂટ';
      case 'bn': return 'অতিরিক্ত ছাড়';
      default: return 'Extra Discount';
    }
  */

  if (!bill) return null;

  // Normalize item properties to fix ₹0 rendering issues
  const safeBill = {
    ...bill,
    items: bill.items?.map(item => {
      const q = parseFloat(item?.quantity || item?.qty || 1);
      const disc = parseFloat(item?.discountValue || item?.discount_value || 0);
      return {
        ...item,
        quantity: q,
        subtotal: item?.subtotal || ((item?.originalPrice || 0) * q),
        totalDiscount: disc * q || item?.totalDiscount || 0,
      };
    })
  };
  bill = safeBill;

  const isPurchase = (bill.bill_type || bill.type || '').toLowerCase() === 'purchase';

  // Define sender and recipient info based on bill type
  const senderInfo = isPurchase ? {
    name: bill.customer?.customerName || 'Supplier',
    address: formatAddr(bill.partyAddress || bill.customer?.address),
    phone: bill.customer?.mobileNo,
    gstin: bill.customer?.gstin,
    logoUrl: null,
    initial: (bill.customer?.customerName?.[0] || 'S')
  } : {
    name: businessProfile?.business_name || 'My Business',
    address: formatAddr(bill.yourAddress || businessProfile?.address),
    phone: businessProfile?.mobile,
    gstin: businessProfile?.gstin,
    logoUrl: businessProfile?.logo_url || businessProfile?.logoUrl,
    initial: (businessProfile?.business_name?.[0] || 'M')
  };

  const recipientInfo = isPurchase ? {
    name: businessProfile?.business_name || 'My Business',
    address: formatAddr(bill.yourAddress || businessProfile?.address),
    phone: businessProfile?.mobile,
    gstin: businessProfile?.gstin
  } : {
    name: bill.customer?.customerName || 'Customer',
    address: formatAddr(bill.partyAddress || bill.customer?.address),
    phone: bill.customer?.mobileNo,
    gstin: bill.customer?.gstin
  };

  const colorOptions = ['#EF4444', '#94A3B8', '#1D4ED8', '#0D9488', '#F59E0B', '#7C3AED'];
  const isPaid = bill.paymentType?.toLowerCase() !== 'unpaid';
  const hasTerms = bill.termsAndConditions && bill.termsAndConditions.trim().length > 0;

  const isGstBill = bill.items?.some(i => (i?.taxAmount || 0) > 0);
  const totalQty = bill.items?.reduce((sum, item) => sum + (item?.quantity || 0), 0) || 0;
  const itemSubtotal = bill.items?.reduce((sum, item) => {
    if (!item) return sum;
    const price = item.originalPrice > 0 ? item.originalPrice : ((item.total || 0) / (item.quantity || 1));
    return sum + ((item.quantity || 0) * (price || 0));
  }, 0) || 0;

  const totalDiscount = bill.items?.reduce((sum, item) => sum + (((item?.discountValue || 0) * (item?.quantity || 1)) || 0), 0) || 0;
  const taxableAmount = bill.items?.reduce((sum, item) => sum + ((item?.subtotal || 0) - (item?.totalDiscount || 0)), 0) || 0;

  const itemsDiscountSum = bill.items?.reduce((sum, item) => sum + (((item?.discountValue || 0) * (item?.quantity || 1)) || item?.totalDiscount || 0), 0) || 0;
  const billTotalDiscount = bill.totalDiscount || bill.total_discount || 0;
  const extraDiscount = billTotalDiscount - itemsDiscountSum > 0.01 ? billTotalDiscount - itemsDiscountSum : 0;

  const totalIgst = bill.items?.reduce((s, i) => s + (i?.igst || 0), 0) || 0;
  const totalTax = bill.items?.reduce((s, i) => s + (i?.taxAmount || 0), 0) || 0;
  const totalCgst = totalIgst > 0 ? 0 : Math.round((bill.items?.reduce((sum, item) => sum + (item?.cgst || 0), 0) || 0) * 100) / 100;
  const totalSgst = totalIgst > 0 ? 0 : Math.round((bill.items?.reduce((sum, item) => sum + (item?.sgst || 0), 0) || 0) * 100) / 100;

  const getTaxSlabs = () => {
    const slabs = {};
    let exemptedValue = 0;
    bill.items?.forEach(item => {
      if (!item) return;
      const postDiscountTaxable = (item.subtotal || 0) - (item.totalDiscount || 0);
      if (postDiscountTaxable <= 0) return;

      if ((item.taxAmount || 0) > 0) {
        const gstRate = ((item.taxAmount || 0) / postDiscountTaxable) * 100;
        const rateKey = (Math.round(gstRate * 100) / 100).toFixed(0);
        if (!slabs[rateKey]) slabs[rateKey] = { taxable: 0, taxAmount: 0, sgst: 0, cgst: 0, igst: 0 };
        slabs[rateKey].taxable += postDiscountTaxable;
        slabs[rateKey].taxAmount += (item.taxAmount || 0);
        slabs[rateKey].sgst += (item.sgst || 0);
        slabs[rateKey].cgst += (item.cgst || 0);
        slabs[rateKey].igst += (item.igst || 0);
      } else {
        exemptedValue += postDiscountTaxable;
      }
    });



    return { slabs, exemptedValue };
  };

  const { slabs: taxSlabs, exemptedValue } = getTaxSlabs();

  const handlePrint = () => {
    window.print();
  };

  const handleShare = async () => {
    const shareText = `*${isPurchase ? 'Purchase Bill' : 'Sale Invoice'}*\n` +
      `*${senderInfo.name}*\n` +
      `---------------------------\n` +
      `*Invoice No:* ${bill.billNumber}\n` +
      `*Date:* ${formatDate(bill.date)}\n` +
      `*To:* ${recipientInfo.name}\n` +
      `*Total Amount:* ${formatCurrency(bill.finalBillAmount)}\n` +
      `*Status:* ${isPaid ? 'PAID (भुगतान किया)' : 'UNPAID (बकाया)'}\n` +
      `---------------------------\n` +
      `Thank you for your business! / हमारे साथ व्यापार करने के लिए धन्यवाद!`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `Invoice #${bill.billNumber}`,
          text: shareText,
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareText);
        alert('Invoice details copied to clipboard! You can now paste and share them on WhatsApp or any other app.\n\nइनवॉइस का विवरण क्लिपबोर्ड पर कॉपी हो गया है! आप इसे व्हाट्सएप या किसी अन्य ऐप पर पेस्ट करके शेयर कर सकते हैं।');
      } catch (err) {
        console.error('Failed to copy text:', err);
      }
    }
  };

  const renderTaxSlabSummary = () => {
    const rateKeys = Object.keys(taxSlabs).sort((a, b) => parseFloat(a) - parseFloat(b));
    if (rateKeys.length === 0 && exemptedValue === 0) return null;

    return (
      <div style={styles.taxSlabBox}>
        <div style={styles.taxSlabHeader}>
          <span style={{ flex: 4 }}>Tax Summary</span>
          <span style={{ flex: 4, textAlign: 'right' }}>Taxable Amount</span>
          <span style={{ flex: 2, textAlign: 'right' }}>Tax</span>
        </div>
        {rateKeys.map(rateStr => {
          const rate = parseFloat(rateStr);
          const data = taxSlabs[rateStr];
          return (
            <React.Fragment key={rateStr}>
              {data.cgst > 0 && (
                <div style={styles.taxSlabRow}>
                  <span style={{ flex: 4 }}>CGST @ {formatRate(rate / 2)}%</span>
                  <span style={{ flex: 4, textAlign: 'right' }}>{formatCurrency(data.taxable)}</span>
                  <span style={{ flex: 2, textAlign: 'right' }}>{formatCurrency(data.cgst)}</span>
                </div>
              )}
              {data.sgst > 0 && (
                <div style={styles.taxSlabRow}>
                  <span style={{ flex: 4 }}>SGST @ {formatRate(rate / 2)}%</span>
                  <span style={{ flex: 4, textAlign: 'right' }}>{formatCurrency(data.taxable)}</span>
                  <span style={{ flex: 2, textAlign: 'right' }}>{formatCurrency(data.sgst)}</span>
                </div>
              )}
              {data.igst > 0 && (
                <div style={styles.taxSlabRow}>
                  <span style={{ flex: 4 }}>IGST @ {formatRate(rate)}%</span>
                  <span style={{ flex: 4, textAlign: 'right' }}>{formatCurrency(data.taxable)}</span>
                  <span style={{ flex: 2, textAlign: 'right' }}>{formatCurrency(data.igst)}</span>
                </div>
              )}
            </React.Fragment>
          )
        })}
        {exemptedValue > 0 && (
          <div style={styles.taxSlabRow}>
            <span style={{ flex: 4 }}>Exempted Supply</span>
            <span style={{ flex: 4, textAlign: 'right' }}>{formatCurrency(exemptedValue)}</span>
            <span style={{ flex: 2, textAlign: 'right' }}>-</span>
          </div>
        )}
      </div>
    );
  };

  const renderPremium = () => (
    <div className="print-full-width print-a4-page" style={styles.paper}>
      <div style={{ ...styles.accentStrip, backgroundColor: accentColor }} />
      <div className="print-premium-content" style={styles.premiumContent}>
        <div style={styles.premiumHeader}>
          <div style={styles.pHeaderLeft}>
            <div style={styles.pLogoContainer}>
              {senderInfo.logoUrl ? <img src={senderInfo.logoUrl} style={styles.pLogo} alt="Logo" /> : <div style={{ ...styles.pLogoPlaceholder, backgroundColor: accentColor }}>{senderInfo.initial}</div>}
            </div>
            <div style={styles.pBizInfo}>
              <h1 style={styles.pBizName}>{senderInfo.name}</h1>
              <p style={styles.pBizMeta}>{senderInfo.address}</p>
              {senderInfo.phone && <p style={styles.pBizMeta}>Phone: {senderInfo.phone}</p>}
              {senderInfo.gstin && <p style={styles.pBizMeta}>GSTIN: {senderInfo.gstin}</p>}
            </div>
          </div>
          <div style={styles.pHeaderRight}>
            <h2 style={styles.pLabel}>{isGstBill ? 'TAX INVOICE' : 'INVOICE'}</h2>
            <div style={styles.pMeta}>
              <p><strong>Invoice No. {bill.billNumber}</strong></p>
              <p>Date: {formatDate(bill.date)}</p>
            </div>
          </div>
        </div>

        <div style={styles.pBillTo}>
          <div style={styles.pBtLeft}>
            <span style={styles.pBtSub}>Bill and Ship To</span>
            <h3 style={styles.pCustName}>{recipientInfo.name}</h3>
            <p style={styles.pCustMeta}>{recipientInfo.address}</p>
            <p style={styles.pCustMeta}>Phone: {recipientInfo.phone}</p>
            {recipientInfo.gstin && <p style={styles.pCustMeta}><strong>GSTIN:</strong> {recipientInfo.gstin}</p>}
          </div>
          <div style={styles.pBtRight}>
            <span style={styles.pBtSub}>Total amount</span>
            <div style={styles.pGrandTotal}>{formatCurrency(bill.finalBillAmount)}</div>
            <p style={styles.pWords}>({numberToWords(bill.finalBillAmount)})</p>
          </div>
        </div>

        {/* GST vs Non-GST Table */}
        <div style={styles.pTable}>
          {isGstBill ? (
            <div style={styles.pTh}>
              <span style={{ flex: 0.5 }}>#</span>
              <span style={{ flex: 3 }}>Item (HSN)</span>
              <span style={{ flex: 1, textAlign: 'center' }}>Qty</span>
              <span style={{ flex: 1.5, textAlign: 'right' }}>Price/Unit</span>
              <span style={{ flex: 1.5, textAlign: 'right' }}>Disc./Unit</span>
              <span style={{ flex: 1.5, textAlign: 'right' }}>Taxable</span>
              <span style={{ flex: 1.5, textAlign: 'right' }}>Total Tax</span>
              <span style={{ flex: 2, textAlign: 'right' }}>Total</span>
            </div>
          ) : (
            <div style={styles.pTh}>
              <span style={{ flex: 0.5 }}>#</span>
              <span style={{ flex: 4 }}>Item Details</span>
              <span style={{ flex: 2, textAlign: 'right' }}>Price/Unit</span>
              <span style={{ flex: 1.5, textAlign: 'right' }}>Disc./Unit</span>
              <span style={{ flex: 1, textAlign: 'center' }}>Qty</span>
              <span style={{ flex: 2, textAlign: 'right' }}>Total</span>
            </div>
          )}

          {bill.items?.map((item, i) => {
            if (!item) return null;

            const pTaxableValue = (item.subtotal || 0) - (item.totalDiscount || 0);
            const pTaxRate = pTaxableValue > 0 ? ((item.taxAmount || 0) / pTaxableValue) * 100 : 0;
            const pDisplayTaxAmount = item.taxAmount || 0;
            const pDisplayTotal = pTaxableValue + pDisplayTaxAmount;

            const taxRate = pTaxableValue > 0 ? (item.taxAmount || 0) / pTaxableValue : 0;
            const displayPrice = (item.taxIncluded || item.item?.taxIncluded)
              ? item.originalPrice * (1 + taxRate)
              : item.originalPrice;
            const displayDiscount = (item.taxIncluded || item.item?.taxIncluded)
              ? (item.totalDiscount / (item.quantity || 1)) * (1 + taxRate)
              : (item.totalDiscount / (item.quantity || 1));

            if (isGstBill) {
              return (
                <div key={i} style={styles.pTr}>
                  <span style={{ flex: 0.5 }}>{i + 1}</span>
                  <span style={{ flex: 3 }}>
                    <div><strong>{item.name || item.item?.name || 'Item'}</strong></div>
                    {item.item?.hsnSacCode && <div style={{ fontSize: '9px', color: '#9CA3AF' }}>HSN: {item.item.hsnSacCode}</div>}
                    {item.taxAmount === 0 && <div style={{ fontSize: '9px', fontStyle: 'italic', color: '#9CA3AF' }}>Exempted</div>}
                  </span>
                  <span style={{ flex: 1, textAlign: 'center' }}>{item.quantity || 0}</span>
                  <span style={{ flex: 1.5, textAlign: 'right' }}>{formatCurrency(displayPrice)}</span>
                  <span style={{ flex: 1.5, textAlign: 'right' }}>
                    {item.totalDiscount > 0 ? (
                      <div>
                        <div>{formatCurrency(displayDiscount)}</div>
                        {item.originalPrice > 0 && <div style={{ fontSize: '9px', color: '#9CA3AF' }}>({formatRate((item.totalDiscount / item.quantity) / item.originalPrice * 100)}%)</div>}
                      </div>
                    ) : '-'}
                  </span>
                  <span style={{ flex: 1.5, textAlign: 'right' }}>{formatCurrency(pTaxableValue)}</span>
                  <span style={{ flex: 1.5, textAlign: 'right' }}>
                    {item.taxAmount > 0 ? (
                      <div>
                        <div>{formatCurrency(pDisplayTaxAmount)}</div>
                        {pTaxableValue > 0 && <div style={{ fontSize: '9px', color: '#9CA3AF' }}>({formatRate(pTaxRate)}%)</div>}
                      </div>
                    ) : '-'}
                  </span>
                  <span style={{ flex: 2, textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(pDisplayTotal)}</span>
                </div>
              );
            }

            const qty = item.quantity || 1;
            const discountValue = item.totalDiscount || 0;
            const originalPrice = item.originalPrice || 0;
            const discPerUnit = discountValue / qty;
            const discPercent = originalPrice > 0 ? (discPerUnit / originalPrice) * 100 : 0;
            const unitStr = item.unit || item.item?.unit || 'PCS';
            const total = item.total || (item.subtotal - item.totalDiscount);

            return (
              <div key={i} style={styles.pTr}>
                <span style={{ flex: 0.5 }}>{i + 1}</span>
                <span style={{ flex: 4 }}><strong>{item.name || item.item?.name || 'Item'}</strong></span>
                <span style={{ flex: 2, textAlign: 'right' }}>{formatCurrency(originalPrice).replace('₹', '')}/{unitStr}</span>
                <span style={{ flex: 1.5, textAlign: 'right' }}>
                  {discountValue > 0 ? (
                    <div>
                      <div>{formatCurrency(discPerUnit).replace('₹', '')}</div>
                      {originalPrice > 0 && <div style={{ fontSize: '9px', color: '#9CA3AF' }}>({formatRate(discPercent)}%)</div>}
                    </div>
                  ) : '-'}
                </span>
                <span style={{ flex: 1, textAlign: 'center' }}>{qty}</span>
                <span style={{ flex: 2, textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(total).replace('₹', '')}</span>
              </div>
            );
          })}

          <div style={styles.pSubtotalTr}>
            {isGstBill ? (
              <>
                <span style={{ flex: 0.5 }} />
                <span style={{ flex: 3, fontWeight: 'bold' }}>Sub-total</span>
                <span style={{ flex: 1, textAlign: 'center', fontWeight: 'bold' }}>{totalQty}</span>
                <span style={{ flex: 1.5 }} />
                <span style={{ flex: 1.5, textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(bill.items?.reduce((sum, item) => {
                  if (!item) return sum;
                  const pTaxableValue = (item.subtotal || 0) - (item.totalDiscount || 0);
                  const taxRate = pTaxableValue > 0 ? (item.taxAmount || 0) / pTaxableValue : 0;
                  const displayDiscount = (item.taxIncluded || item.item?.taxIncluded)
                    ? (item.totalDiscount / (item.quantity || 1)) * (1 + taxRate)
                    : (item.totalDiscount / (item.quantity || 1));
                  return sum + (displayDiscount * (item.quantity || 1));
                }, 0) || 0)}</span>
                <span style={{ flex: 1.5, textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(taxableAmount)}</span>
                <span style={{ flex: 1.5, textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(totalTax)}</span>
                <span style={{ flex: 2, textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(taxableAmount + totalTax)}</span>
              </>
            ) : (
              <>
                <span style={{ flex: 4.5, fontWeight: 'bold' }}>Sub-total</span>
                <span style={{ flex: 2 }} />
                <span style={{ flex: 1.5, textAlign: 'right', fontWeight: 'bold' }}>{totalDiscount > 0 ? formatCurrency(totalDiscount).replace('₹', '') : '-'}</span>
                <span style={{ flex: 1, textAlign: 'center', fontWeight: 'bold' }}>{totalQty}</span>
                <span style={{ flex: 2, textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(itemSubtotal - totalDiscount).replace('₹', '')}</span>
              </>
            )}
          </div>
        </div>

        {isGstBill && renderTaxSlabSummary()}

        <div className="print-summary-area" style={styles.pSummaryArea}>
          <div style={styles.pSumLeft}>
            {hasTerms && (
              <>
                <div style={styles.pTermsTitle}>Terms & conditions</div>
                <div style={styles.pTermsText}>{bill.termsAndConditions}</div>
              </>
            )}
          </div>

          <div style={styles.pSumRight}>
            <div style={styles.pSumRow}><span style={{ color: '#6B7280' }}>Taxable Amount</span> <span style={{ color: '#6B7280' }}>{formatCurrency(taxableAmount)}</span></div>
            {extraDiscount > 0 && (
              <div style={styles.pSumRow}>
                <span style={{ color: '#6B7280' }}>Extra Discount</span>
                <span style={{ color: '#6B7280' }}>- {formatCurrency(extraDiscount)}</span>
              </div>
            )}
            {isGstBill && (
              <>
                {totalCgst > 0 && <div style={styles.pSumRow}><span>Total CGST</span> <span>{formatCurrency(totalCgst)}</span></div>}
                {totalSgst > 0 && <div style={styles.pSumRow}><span>Total SGST</span> <span>{formatCurrency(totalSgst)}</span></div>}
                {totalIgst > 0 && <div style={styles.pSumRow}><span>Total IGST</span> <span>{formatCurrency(totalIgst)}</span></div>}
              </>
            )}
            <div style={{ height: '2px', backgroundColor: '#E2E8F0', margin: '8px 0' }} />
            <div style={styles.pSumRow}><strong>Total amount</strong> <strong>{formatCurrency(bill.finalBillAmount)}</strong></div>
            {isPaid && (
              <>
                <div style={styles.pSumRow}><span>Received</span> <span>{formatCurrency(bill.finalBillAmount)}</span></div>
                <div style={styles.pSumRow}><strong>Balance</strong> <strong>{formatCurrency(0)}</strong></div>
              </>
            )}
            <p style={{ ...styles.pWords, textAlign: 'right', marginTop: '6px' }}>{numberToWords(bill.finalBillAmount)}</p>
          </div>

          {/* PAID/UNPAID Stamp next to summary details */}
          {isPaid ? (
            <div style={styles.pPaidStamp}>
              <div style={styles.pStampCircle}>
                <div style={styles.pStampText}>PAID</div>
              </div>
            </div>
          ) : (
            <div style={{ ...styles.pPaidStamp, opacity: 0.7 }}>
              <div style={{ ...styles.pStampCircle, borderColor: '#DC2626', color: '#DC2626' }}>
                <div style={{ ...styles.pStampText, borderColor: '#DC2626' }}>UNPAID</div>
              </div>
            </div>
          )}
        </div>

        <div className="print-footer-container">
          <div style={{ ...styles.pDigitalMsg, marginTop: '24px', marginBottom: '16px' }}>- THIS IS A DIGITALLY CREATED INVOICE -</div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '20px', marginBottom: '8px' }}>
            <div style={styles.pTrust}>Thank you for your trust in us</div>
            <div style={styles.pSigBox}>
              <div style={styles.pSigLine} />
              <span style={styles.pSigLabel}>AUTHORISED SIGNATURE</span>
            </div>
          </div>

          <div style={{ textAlign: 'center', fontSize: '10px', color: '#9CA3AF', marginTop: '16px', borderTop: '1px dashed #E2E8F0', paddingTop: '8px' }}>
            Created by Moxpal Khata
          </div>
        </div>
      </div>
      <div style={{ ...styles.accentStrip, backgroundColor: accentColor }} />
    </div>
  );

  const renderThermal = () => (
    <div className="print-full-width print-thermal-page" style={styles.thermalPaper}>
      <h2 style={styles.tBizName}>{senderInfo.name.toUpperCase()}</h2>
      <p style={styles.tBizMeta}>{senderInfo.address}</p>
      {senderInfo.phone && <p style={styles.tBizMeta}>Phone: {senderInfo.phone}</p>}
      {senderInfo.gstin && <p style={styles.tBizMeta}>GSTIN: {senderInfo.gstin}</p>}

      <div style={styles.tDivider}>----------------------------------------</div>
      <div style={{ ...styles.tCenter, fontWeight: 'bold' }}>BILL AND SHIP TO</div>
      <div style={styles.tCenter}>{recipientInfo.name}</div>
      <div style={styles.tCenter}>{recipientInfo.address}</div>
      {recipientInfo.phone && <div style={styles.tCenter}>Phone: {recipientInfo.phone}</div>}
      {recipientInfo.gstin && <div style={styles.tCenter}>GSTIN: {recipientInfo.gstin}</div>}

      <div style={styles.tDivider}>----------------------------------------</div>
      <div style={styles.tMetaRow}><span>Invoice No.: {bill.billNumber}</span> <span>Date: {formatDate(bill.date)}</span></div>

      <div style={styles.tDivider}>----------------------------------------</div>
      <div style={styles.tTh}>
        <span style={{ flex: 5 }}>Item</span>
        <span style={{ flex: 1, textAlign: 'center' }}>Qty</span>
        <span style={{ flex: 2, textAlign: 'right' }}>Amount</span>
      </div>
      <div style={styles.tDivider}>----------------------------------------</div>

      {bill.items?.map((item, i) => {
        if (!item) return null;
        const taxableValue = (item.subtotal || 0) - (item.totalDiscount || 0);
        const displayTotal = isGstBill
          ? (taxableValue + (item.taxAmount || 0))
          : (item.total || (item.subtotal - item.totalDiscount));

        return (
          <div key={i} style={{ padding: '4px 0' }}>
            <div style={styles.tTr}>
              <span style={{ flex: 5 }}>
                {item.name || item.item?.name}
                {item.item?.hsnSacCode && <div style={{ fontSize: '8px' }}>HSN: {item.item.hsnSacCode}</div>}
                {item.taxAmount === 0 && isGstBill && <div style={{ fontSize: '8px', fontStyle: 'italic' }}>(Exempted)</div>}
              </span>
              <span style={{ flex: 1, textAlign: 'center' }}>{item.quantity}</span>
              <span style={{ flex: 2, textAlign: 'right' }}>{formatCurrency(displayTotal)}</span>
            </div>
          </div>
        )
      })}

      <div style={styles.tDivider}>----------------------------------------</div>
      <div style={styles.tSumRow}><strong>Taxable Value:</strong> <strong>{formatCurrency(taxableAmount)}</strong></div>
      {extraDiscount > 0 && (
        <div style={styles.tSumRow}>
          <span>Extra Discount:</span>
          <span>- {formatCurrency(extraDiscount)}</span>
        </div>
      )}

      {isGstBill && Object.keys(taxSlabs).map(rateStr => {
        const rate = parseFloat(rateStr);
        const data = taxSlabs[rateStr];
        return (
          <React.Fragment key={rateStr}>
            {data.cgst > 0 && <div style={styles.tSumRow}><span>CGST @ {formatRate(rate / 2)}%:</span> <span>{formatCurrency(data.cgst)}</span></div>}
            {data.sgst > 0 && <div style={styles.tSumRow}><span>SGST @ {formatRate(rate / 2)}%:</span> <span>{formatCurrency(data.sgst)}</span></div>}
            {data.igst > 0 && <div style={styles.tSumRow}><span>IGST @ {formatRate(rate)}%:</span> <span>{formatCurrency(data.igst)}</span></div>}
          </React.Fragment>
        )
      })}

      <div style={styles.tSumRow}><strong>Net Amount:</strong> <strong>{formatCurrency(bill.finalBillAmount)}</strong></div>

      {hasTerms && (
        <>
          <div style={styles.tDivider}>----------------------------------------</div>
          <div style={{ fontWeight: 'bold', fontSize: '10px', marginTop: '8px' }}>Terms & Conditions</div>
          <div style={{ fontSize: '10px', whiteSpace: 'pre-wrap' }}>{bill.termsAndConditions}</div>
        </>
      )}

      <div style={styles.tFooter}>Thank you for your trust in us</div>
    </div>
  );

  const renderBasic = () => (
    <div className="print-full-width print-a4-page" style={styles.paper}>
      <div style={{ ...styles.accentStrip, backgroundColor: accentColor }} />
      <div className="print-premium-content" style={{ padding: '24px 24px 12px 24px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ ...styles.basicHeader, padding: '0 0 12px 0' }}>
          <div style={styles.bHeaderLeft}>
            <h1 style={{ ...styles.bBizName, fontSize: '14px' }}>{senderInfo.name}</h1>
            <p style={{ ...styles.bBizMeta, fontSize: '10px' }}>{senderInfo.address}</p>
            {senderInfo.phone && <p style={{ ...styles.bBizMeta, fontSize: '10px' }}>Phone: {senderInfo.phone}</p>}
            {senderInfo.gstin && <p style={{ ...styles.bBizMeta, fontSize: '10px' }}>GSTIN: {senderInfo.gstin}</p>}
          </div>
          <div style={styles.bHeaderRight}>
            <h2 style={{ ...styles.bLabel, fontSize: '12px', color: accentColor }}>{isGstBill ? 'TAX INVOICE' : (isPurchase ? 'Original / Purchase Bill' : 'Original / Sale Bill')}</h2>
            <p style={{ ...styles.bMeta, fontSize: '10px' }}>Bill no. {bill.billNumber}</p>
            <p style={{ ...styles.bMeta, fontSize: '10px' }}>Date: {formatDate(bill.date)}</p>
          </div>
        </div>

      <div style={{ borderBottom: '1px solid #E2E8F0', margin: '8px 0' }} />

      <div style={{ border: '1px solid #E2E8F0', padding: '8px', marginBottom: '12px' }}>
        <div style={{ fontSize: '10px', fontWeight: 'bold', color: '#6B7280', marginBottom: '4px' }}>BILL TO / SHIP TO</div>
        <p style={{ fontSize: '11px', fontWeight: 'bold', margin: '2px 0' }}>{recipientInfo.name}</p>
        <p style={{ fontSize: '10px', margin: '2px 0' }}>{recipientInfo.address}</p>
        {recipientInfo.phone && <p style={{ fontSize: '10px', margin: '2px 0' }}>Phone: {recipientInfo.phone}</p>}
        {recipientInfo.gstin && <p style={{ fontSize: '10px', margin: '2px 0' }}><strong>GSTIN:</strong> {recipientInfo.gstin}</p>}
      </div>

      {/* BASIC GST / NON GST TABLE */}
      {isGstBill ? (
        <div>
          <div style={{ display: 'flex', backgroundColor: `${accentColor}12`, border: `1px solid ${accentColor}25`, borderBottom: `2px solid ${accentColor}`, padding: '6px 4px' }}>
            <span style={{ flex: 0.5, fontSize: '9px', fontWeight: 'bold' }}>NO</span>
            <span style={{ flex: 2, fontSize: '9px', fontWeight: 'bold' }}>ITEMS / HSN</span>
            <span style={{ flex: 1, fontSize: '9px', fontWeight: 'bold', textAlign: 'center' }}>QTY</span>
            <span style={{ flex: 1.5, fontSize: '9px', fontWeight: 'bold', textAlign: 'right' }}>RATE</span>
            <span style={{ flex: 1.5, fontSize: '9px', fontWeight: 'bold', textAlign: 'right' }}>DISC./UNIT</span>
            <span style={{ flex: 1.5, fontSize: '9px', fontWeight: 'bold', textAlign: 'right' }}>TAXABLE</span>
            <span style={{ flex: 1.5, fontSize: '9px', fontWeight: 'bold', textAlign: 'right' }}>TAX</span>
            <span style={{ flex: 2, fontSize: '9px', fontWeight: 'bold', textAlign: 'right' }}>AMOUNT</span>
          </div>
          {bill.items?.map((item, i) => {
            if (!item) return null;
            const bTaxableValue = (item.subtotal || 0) - (item.totalDiscount || 0);
            const bTaxRate = bTaxableValue > 0 ? ((item.taxAmount || 0) / bTaxableValue) * 100 : 0;
            const bDisplayTotal = bTaxableValue + (item.taxAmount || 0);
            const taxRate = bTaxableValue > 0 ? (item.taxAmount || 0) / bTaxableValue : 0;
            const displayPrice = (item.taxIncluded || item.item?.taxIncluded)
              ? item.originalPrice * (1 + taxRate)
              : item.originalPrice;
            const displayDiscount = (item.taxIncluded || item.item?.taxIncluded)
              ? (item.totalDiscount / (item.quantity || 1)) * (1 + taxRate)
              : (item.totalDiscount / (item.quantity || 1));

            return (
              <div key={i} style={{ display: 'flex', borderBottom: '1px solid #E2E8F0', borderLeft: '1px solid #E2E8F0', borderRight: '1px solid #E2E8F0', padding: '6px 4px', fontSize: '9px' }}>
                <span style={{ flex: 0.5 }}>{i + 1}</span>
                <span style={{ flex: 2, fontWeight: 'bold' }}>
                  <div>{item.name || item.item?.name}</div>
                  {item.item?.hsnSacCode && <div style={{ fontSize: '8px', fontWeight: 'normal', color: '#6B7280' }}>HSN: {item.item.hsnSacCode}</div>}
                  {item.taxAmount === 0 && <div style={{ fontSize: '8px', fontWeight: 'normal', fontStyle: 'italic', color: '#6B7280' }}>Exempted</div>}
                </span>
                <span style={{ flex: 1, textAlign: 'center' }}>{item.quantity}</span>
                <span style={{ flex: 1.5, textAlign: 'right' }}>{formatCurrency(displayPrice)}</span>
                <span style={{ flex: 1.5, textAlign: 'right' }}>{item.totalDiscount > 0 ? formatCurrency(displayDiscount) : '-'}</span>
                <span style={{ flex: 1.5, textAlign: 'right' }}>{formatCurrency(bTaxableValue)}</span>
                <span style={{ flex: 1.5, textAlign: 'right' }}>
                  {item.taxAmount > 0 ? (
                    <div>
                      <div>{formatCurrency(item.taxAmount)}</div>
                      {bTaxableValue > 0 && <div style={{ fontSize: '8px', color: '#6B7280' }}>@{formatRate(bTaxRate)}%</div>}
                    </div>
                  ) : '-'}
                </span>
                <span style={{ flex: 2, textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(bDisplayTotal)}</span>
              </div>
            )
          })}
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', backgroundColor: `${accentColor}12`, border: `1px solid ${accentColor}25`, borderBottom: `2px solid ${accentColor}`, padding: '6px 4px' }}>
            <span style={{ flex: 0.5, fontSize: '9px', fontWeight: 'bold' }}>S.NO</span>
            <span style={{ flex: 3, fontSize: '9px', fontWeight: 'bold' }}>ITEMS</span>
            <span style={{ flex: 1, fontSize: '9px', fontWeight: 'bold', textAlign: 'center' }}>QTY</span>
            <span style={{ flex: 1.5, fontSize: '9px', fontWeight: 'bold', textAlign: 'right' }}>RATE</span>
            <span style={{ flex: 1.5, fontSize: '9px', fontWeight: 'bold', textAlign: 'right' }}>DISC./UNIT</span>
            <span style={{ flex: 2, fontSize: '9px', fontWeight: 'bold', textAlign: 'right' }}>AMOUNT</span>
          </div>
          {bill.items?.map((item, i) => {
            if (!item) return null;
            const total = item.total || (item.subtotal - item.totalDiscount);
            return (
              <div key={i} style={{ display: 'flex', borderBottom: '1px solid #E2E8F0', borderLeft: '1px solid #E2E8F0', borderRight: '1px solid #E2E8F0', padding: '6px 4px', fontSize: '9px' }}>
                <span style={{ flex: 0.5 }}>{i + 1}</span>
                <span style={{ flex: 3, fontWeight: 'bold' }}>{item.name || item.item?.name}</span>
                <span style={{ flex: 1, textAlign: 'center' }}>{item.quantity}</span>
                <span style={{ flex: 1.5, textAlign: 'right' }}>{formatCurrency(item.originalPrice)}</span>
                <span style={{ flex: 1.5, textAlign: 'right' }}>{item.totalDiscount > 0 ? formatCurrency(item.totalDiscount / item.quantity) : '-'}</span>
                <span style={{ flex: 2, textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(total)}</span>
              </div>
            )
          })}
        </div>
      )}

      {isGstBill && renderTaxSlabSummary()}

      <div className="print-summary-area" style={{ border: '1px solid #E2E8F0', padding: '16px', marginTop: '16px', borderRadius: '8px', backgroundColor: '#F8FAFC' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', padding: '4px 0' }}>
          <span style={{ color: '#4B5563' }}>Taxable Amount</span>
          <span style={{ color: '#1F2937', fontWeight: '500' }}>{formatCurrency(taxableAmount)}</span>
        </div>
        {extraDiscount > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', padding: '4px 0' }}>
            <span style={{ color: '#4B5563' }}>Extra Discount</span>
            <span style={{ color: '#1F2937', fontWeight: '500' }}>- {formatCurrency(extraDiscount)}</span>
          </div>
        )}
        {isGstBill && totalCgst > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', padding: '4px 0' }}>
            <span style={{ color: '#4B5563' }}>Total CGST</span>
            <span style={{ color: '#1F2937', fontWeight: '500' }}>{formatCurrency(totalCgst)}</span>
          </div>
        )}
        {isGstBill && totalSgst > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', padding: '4px 0' }}>
            <span style={{ color: '#4B5563' }}>Total SGST</span>
            <span style={{ color: '#1F2937', fontWeight: '500' }}>{formatCurrency(totalSgst)}</span>
          </div>
        )}
        {isGstBill && totalIgst > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', padding: '4px 0' }}>
            <span style={{ color: '#4B5563' }}>Total IGST</span>
            <span style={{ color: '#1F2937', fontWeight: '500' }}>{formatCurrency(totalIgst)}</span>
          </div>
        )}

        <div style={{ height: '1px', backgroundColor: '#E2E8F0', margin: '8px 0' }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '8px 10px', margin: '4px -10px', backgroundColor: `${accentColor}08`, borderRadius: '4px', borderTop: `1px solid ${accentColor}15`, borderBottom: `1px solid ${accentColor}15`, color: '#0F172A' }}>
          <strong style={{ fontWeight: 'bold' }}>TOTAL</strong>
          <strong style={{ fontWeight: 'bold', color: accentColor }}>{formatCurrency(bill.finalBillAmount)}</strong>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', padding: '4px 0', marginTop: '6px' }}>
          <span style={{ color: '#4B5563' }}>Received Amount</span>
          <span style={{ fontWeight: '600', color: '#16A34A' }}>{formatCurrency(isPaid ? bill.finalBillAmount : 0)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', padding: '4px 0' }}>
          <span style={{ color: '#4B5563' }}>Invoice Balance</span>
          <span style={{ fontWeight: '600', color: isPaid ? '#4B5563' : '#DC2626' }}>{formatCurrency(isPaid ? 0 : bill.finalBillAmount)}</span>
        </div>

        <div style={{ borderTop: '1px dashed #E2E8F0', marginTop: '10px', paddingTop: '8px', display: 'flex', fontSize: '9px' }}>
          <span style={{ fontWeight: 'bold', color: '#6B7280' }}>In Words: </span>
          <span style={{ fontStyle: 'italic', marginLeft: '4px', color: '#4B5563' }}>{numberToWords(bill.finalBillAmount)}</span>
        </div>
      </div>

      {hasTerms && (
        <div style={{ border: '1px solid #E2E8F0', padding: '10px', marginTop: '12px', borderRadius: '6px' }}>
          <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '4px', color: '#4B5563' }}>Terms & conditions</div>
          <div style={{ fontSize: '9px', color: '#6B7280', whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>{bill.termsAndConditions}</div>
        </div>
      )}

      <div className="print-footer-container" style={{ marginTop: 'auto', paddingTop: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '24px', padding: '0 8px' }}>
          <div style={{ fontSize: '11px', color: '#6B7280', fontStyle: 'italic' }}>Thank you for your business!</div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: '160px', borderTop: '1px solid #111827', margin: '0 auto 4px auto' }} />
            <span style={{ fontSize: '9px', fontWeight: 'bold', color: '#1F2937' }}>AUTHORISED SIGNATORY</span>
          </div>
        </div>

        <div style={{ textAlign: 'center', fontSize: '10px', color: '#9CA3AF', borderTop: '1px dashed #E2E8F0', paddingTop: '8px' }}>
          Created by Moxpal Khata
        </div>
      </div>
      </div>
      <div style={{ ...styles.accentStrip, backgroundColor: accentColor }} />
    </div>
  );

  let invoiceContent = null;
  try {
    if (selectedTheme === 'premium') invoiceContent = renderPremium();
    else if (selectedTheme === 'thermal') invoiceContent = renderThermal();
    else if (selectedTheme === 'basic') invoiceContent = renderBasic();
  } catch (error) {
    invoiceContent = (
      <div style={{ padding: '24px', backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: '8px', border: '1px solid #F87171' }}>
        <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: 'bold' }}>Error Generating Preview</h3>
        <p style={{ margin: 0, fontSize: '14px' }}>Something went wrong while loading the invoice details. Please ensure all bill items and customer data are valid.</p>
        <pre style={{ marginTop: '12px', fontSize: '11px', overflowX: 'auto' }}>{error.message}</pre>
      </div>
    );
  }

  return (
    <div className="print-preview-container" style={styles.container}>
      <div className="no-print" style={styles.nav}>
        <button style={styles.backBtn} onClick={onBack}><ArrowLeft size={20} /> Invoice Preview</button>
        <div style={styles.navRight}>
          <button style={styles.navIcon} onClick={() => setShowDeleteConfirm(true)}><Trash2 size={20} /></button>
          <button style={styles.navIcon} onClick={onEdit}><Edit size={20} /></button>
        </div>
      </div>

      <div className="print-preview-content" style={styles.content}>
        <div className="print-scroll-area" style={styles.scrollArea}>
          {invoiceContent}
        </div>

        <div className="no-print" style={styles.controls}>
          <div style={styles.themeSelector}>
            <button style={{ ...styles.tBtn, ...(selectedTheme === 'premium' ? styles.tBtnActive : {}) }} onClick={() => setSelectedTheme('premium')}>Premium <span style={styles.newBadge}>New</span></button>
            <button style={{ ...styles.tBtn, ...(selectedTheme === 'thermal' ? styles.tBtnActive : {}) }} onClick={() => setSelectedTheme('thermal')}>Thermal</button>
            <button style={{ ...styles.tBtn, ...(selectedTheme === 'basic' ? styles.tBtnActive : {}) }} onClick={() => setSelectedTheme('basic')}>Basic</button>
          </div>

          <div style={styles.colorPicker}>
            {colorOptions.map(c => (
              <button key={c} style={{ ...styles.colorCircle, backgroundColor: c }} onClick={() => setAccentColor(c)}>
                {accentColor === c && <Check size={14} color="white" />}
              </button>
            ))}
          </div>

          <div style={styles.actionButtons}>
            <div style={styles.actionItem} onClick={handlePrint}><button style={styles.circleAction}><Download size={22} /></button><span style={{ fontSize: '13px', fontWeight: '600', color: '#4B5563' }}>Download</span></div>
            <div style={styles.actionItem} onClick={handleShare}><button style={styles.circleAction}><Share2 size={22} /></button><span style={{ fontSize: '13px', fontWeight: '600', color: '#4B5563' }}>Share</span></div>
            <div style={styles.actionItem} onClick={handlePrint}><div style={styles.circleAction}><Printer size={22} /></div><span style={{ fontSize: '13px', fontWeight: '600', color: '#4B5563' }}>Print</span></div>
          </div>

          <div style={styles.bottomNav}>
            <button style={styles.secondaryBtn} onClick={onCreateNew}>Create New</button>
            <button style={styles.primaryBtn} onClick={onBack}>Done</button>
          </div>
        </div>
      </div>

      {/* DELETE CONFIRMATION MODAL */}
      {showDeleteConfirm && (
        <div style={styles.modalOverlay}>
          <div style={styles.confirmBox}>
            <h4 style={styles.confirmTitle}>{t(LangKeys.bill_details_delete_confirmation_title)}</h4>
            <p style={styles.confirmText}>{t(LangKeys.bill_details_delete_confirmation_content)}</p>
            <div style={styles.confirmActions}>
              <button style={styles.cancelBtn} onClick={() => setShowDeleteConfirm(false)}>{t(LangKeys.cancel).toUpperCase()}</button>
              <button style={styles.deleteBtn} onClick={() => {
                setShowDeleteConfirm(false);
                onDelete();
              }}>{t(LangKeys.bill_details_delete_button).toUpperCase()}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicePreviewView;
